import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  useCreateOrUpdateSetting,
  useGetSettings,
} from 'queries/settings.query';
import { useEffect, useState } from 'react';
import Dropzone from './Dropzone';
import { getStorageImageUrl } from 'utils/imageUrl';

export default function SiteSettingsForm() {
  const inputColor = useColorModeValue('gray.800', 'white');
  const backgroundColor = useColorModeValue('white', 'gray.700');

  const toast = useToast();
  const { data: settingsData, isLoading } = useGetSettings();
  const { mutate: createOrUpdateSetting, isPending } =
    useCreateOrUpdateSetting();

  const [formState, setFormState] = useState({
    site_name: '',
    site_address: '',
    site_phone: '',
    site_email: '',
    site_copyright: '',
    youtube_url: '',
    tiktok_url: '',
    instagram_url: '',
    facebook_url: '',
  });

  const [newFileState, setNewFileState] = useState({
    site_sm_logo: null,
    site_lg_logo: null,
    site_favicon: null,
  });

  const [currentFileUrls, setCurrentFileUrls] = useState({
    site_sm_logo: null,
    site_lg_logo: null,
    site_favicon: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (settingsData) {
      setFormState({
        site_name: settingsData.site_name || '',
        site_address: settingsData.site_address || '',
        site_phone: settingsData.site_phone || '',
        site_email: settingsData.site_email || '',
        site_copyright: settingsData.site_copyright || '',
        youtube_url: settingsData.youtube_url || '',
        tiktok_url: settingsData.tiktok_url || '',
        instagram_url: settingsData.instagram_url || '',
        facebook_url: settingsData.facebook_url || '',
      });
      setCurrentFileUrls({
        site_sm_logo: getStorageImageUrl(settingsData.site_sm_logo),
        site_lg_logo: getStorageImageUrl(settingsData.site_lg_logo),
        site_favicon: getStorageImageUrl(settingsData.site_favicon),
      });
      setNewFileState({
        site_sm_logo: null,
        site_lg_logo: null,
        site_favicon: null,
      });
    } else {
      setFormState({
        site_name: '',
        site_address: '',
        site_phone: '',
        site_email: '',
        site_copyright: '',
        youtube_url: '',
        tiktok_url: '',
        instagram_url: '',
        facebook_url: '',
      });
      setCurrentFileUrls({
        site_sm_logo: null,
        site_lg_logo: null,
        site_favicon: null,
      });
      setNewFileState({
        site_sm_logo: null,
        site_lg_logo: null,
        site_favicon: null,
      });
    }
    setErrors({});
  }, [settingsData]);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const handleFileChange = (name, file) => {
    setNewFileState({ ...newFileState, [name]: file });
    setErrors({ ...errors, [name]: null });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const formData = new FormData();
    Object.keys(formState).forEach((key) => {
      formData.append(key, formState[key]);
    });

    if (newFileState.site_sm_logo) {
      formData.append('site_sm_logo', newFileState.site_sm_logo);
    }
    if (newFileState.site_lg_logo) {
      formData.append('site_lg_logo', newFileState.site_lg_logo);
    }
    if (newFileState.site_favicon) {
      formData.append('site_favicon', newFileState.site_favicon);
    }

    createOrUpdateSetting(formData, {
      onSuccess: () => {
        toast({
          title: 'Settings saved.',
          description: 'Your site settings have been updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
      onError: (error) => {
        const errorMessages = error?.response?.data?.errors || {};
        setErrors(errorMessages);
        toast({
          title: 'Validation Error',
          description:
            error?.response?.data?.message ||
            'Please check the form for errors.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="brand.500" />
        <Heading size="md" mt={4}>
          Loading Settings...
        </Heading>
      </Box>
    );
  }

  return (
    <Card>
      <Box p={6}>
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          Site Settings
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            {/* Grouped Fields */}
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl
                id="site_name"
                isInvalid={!!errors.site_name}
                flex="1"
              >
                <FormLabel>Site Name *</FormLabel>
                <Input
                  name="site_name"
                  value={formState.site_name}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.site_name && errors.site_name[0]}
                </FormErrorMessage>
              </FormControl>
              {/* <FormControl
                id="site_copyright"
                isInvalid={!!errors.site_copyright}
                flex="1"
              >
                <FormLabel>Site Copyright *</FormLabel>
                <Input
                  name="site_copyright"
                  value={formState.site_copyright}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.site_copyright && errors.site_copyright[0]}
                </FormErrorMessage>
              </FormControl> */}
            </Flex>

            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl
                id="site_email"
                isInvalid={!!errors.site_email}
                flex="1"
              >
                <FormLabel>Contact Email *</FormLabel>
                <Input
                  type="email"
                  name="site_email"
                  value={formState.site_email}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.site_email && errors.site_email[0]}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                id="site_phone"
                isInvalid={!!errors.site_phone}
                flex="1"
              >
                <FormLabel>Contact Phone *</FormLabel>
                <Input
                  name="site_phone"
                  value={formState.site_phone}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.site_phone && errors.site_phone[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>

            {/* Address Field */}
            <FormControl id="site_address" isInvalid={!!errors.site_address}>
              <FormLabel>Office Location</FormLabel>
              <Input
                name="site_address"
                value={formState.site_address}
                onChange={handleChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              <FormErrorMessage>
                {errors.site_address && errors.site_address[0]}
              </FormErrorMessage>
            </FormControl>

            {/* Social Links */}
            <Heading size="md" mb={4} mt={6}>
              Social Links
            </Heading>
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl id="youtube_url" isInvalid={!!errors.youtube_url} flex="1">
                <FormLabel>YouTube Channel URL</FormLabel>
                <Input
                  name="youtube_url"
                  value={formState.youtube_url}
                  onChange={handleChange}
                  placeholder="https://youtube.com/..."
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.youtube_url && errors.youtube_url[0]}
                </FormErrorMessage>
              </FormControl>
              
              <FormControl id="tiktok_url" isInvalid={!!errors.tiktok_url} flex="1">
                <FormLabel>TikTok Profile URL</FormLabel>
                <Input
                  name="tiktok_url"
                  value={formState.tiktok_url}
                  onChange={handleChange}
                  placeholder="https://tiktok.com/@..."
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.tiktok_url && errors.tiktok_url[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>

            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl id="instagram_url" isInvalid={!!errors.instagram_url} flex="1">
                <FormLabel>Instagram Profile URL</FormLabel>
                <Input
                  name="instagram_url"
                  value={formState.instagram_url}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.instagram_url && errors.instagram_url[0]}
                </FormErrorMessage>
              </FormControl>
              
              <FormControl id="facebook_url" isInvalid={!!errors.facebook_url} flex="1">
                <FormLabel>Facebook Page URL</FormLabel>
                <Input
                  name="facebook_url"
                  value={formState.facebook_url}
                  onChange={handleChange}
                  placeholder="https://facebook.com/..."
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.facebook_url && errors.facebook_url[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>

            {/* File Uploads */}
            <Heading size="md" mb={4} mt={6}>
              Logos & Branding
            </Heading>
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              {[
                // { name: 'site_sm_logo', label: 'Small Logo' },
                { name: 'site_lg_logo', label: 'Large Logo' },
                { name: 'site_favicon', label: 'Favicon' },
              ].map((field) => (
                <FormControl
                  key={field.name}
                  id={field.name}
                  flex="1"
                  isInvalid={!!errors[field.name]}
                >
                  <FormLabel>{field.label}</FormLabel>
                  {/* REMOVED THE REDUNDANT IMAGE COMPONENT HERE */}
                  <Dropzone
                    title={field.label}
                    onFileChange={(file) => handleFileChange(field.name, file)}
                    // Pass current preview from new file or existing URL
                    preview={
                      newFileState[field.name]
                        ? URL.createObjectURL(newFileState[field.name])
                        : currentFileUrls[field.name] // Fallback to existing URL
                    }
                  />
                  <FormErrorMessage>
                    {errors[field.name] && errors[field.name][0]}
                  </FormErrorMessage>
                </FormControl>
              ))}
            </Flex>

            <Button
              mt={4}
              colorScheme="blue"
              type="submit"
              isLoading={isPending}
              // alignSelf={{ base: 'stretch', md: 'flex-start' }}
            >
              Save Changes
            </Button>
          </VStack>
        </form>
      </Box>
    </Card>
  );
}
